import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
    { label: "Chi siamo", href: "#chi-siamo", testId: "nav-link-chi-siamo" },
    { label: "Servizi", href: "#servizi", testId: "nav-link-servizi" },
    { label: "Portfolio", href: "#portfolio", testId: "nav-link-portfolio" },
    { label: "Recensioni", href: "#recensioni", testId: "nav-link-recensioni" },
    { label: "Contatti", href: "#contatti", testId: "nav-link-contatti" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNav = (e, href) => {
        if (pathname !== "/") return; // allow default Link behavior on project pages
        e.preventDefault();
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled || open
                    ? "bg-ink-800/90 backdrop-blur-md border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src="/logo-dogaparquet.png"
                        alt="Dogaparquet di Gianluca Occhipinti"
                        className="h-11 lg:h-12 w-auto object-contain transition-opacity duration-500 group-hover:opacity-80"
                    />
                    <span className="hidden md:inline text-[10px] uppercase tracking-widest text-brass/80 border-l border-white/15 pl-3">
                        Modica · RG
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {pathname === "/" ? (
                        NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={l.testId}
                                onClick={(e) => handleNav(e, l.href)}
                                className="link-underline text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-500"
                            >
                                {l.label}
                            </a>
                        ))
                    ) : (
                        <Link
                            to="/#portfolio"
                            data-testid="nav-back-home"
                            className="link-underline text-sm tracking-wide text-cream/85"
                        >
                            ← Torna al sito
                        </Link>
                    )}
                </nav>

                <a
                    href="tel:+393891983122"
                    data-testid="nav-phone"
                    className="hidden lg:inline-flex items-center text-sm text-brass hover:text-brass-light tracking-wide transition-colors duration-500"
                >
                    389 198 3122
                </a>

                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden p-2 text-cream"
                    aria-label="Apri menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-ink-800 border-t border-white/5"
                >
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={(e) => handleNav(e, l.href)}
                                className="font-serif text-2xl text-cream"
                                data-testid={`${l.testId}-mobile`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="tel:+393891983122"
                            className="text-brass text-sm tracking-widest uppercase pt-4 border-t border-white/10"
                            data-testid="nav-phone-mobile"
                        >
                            Chiama — 389 198 3122
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
